/* Copyright (c) 2020-present CloudIO Inc */

import { toolTipProps } from 'componentregistry/utils/commonProps';
import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { badgeVariant } from '../../registry/constants';
import { defaultValue } from './Badge';

const name = 'Badge';

const BadgeManager: ComponentManagerFn<'Badge'> = (
  platform: Platform,
): ComponentManager<'Badge'> => {
  return {
    ...baseComponentManager<'Badge'>(platform, 'Badge'),
    type: 'Badge',
    name,
    isChildAllowed: (page, item, childType, { appUid, pageId, itemId }) => {
      const children = platform.getChildrenItems(
        appUid,
        pageId,
        page,
        item,
        (c) => c.type !== 'TriggerOnClick',
      );
      return (
        (
          [
            'Box',
            'Button',
            'Card',
            'Icon',
            'IconButton',
            'Link',
            'TriggerOnClick',
          ] as ItemType[]
        ).includes(childType) &&
        (children.length === 0 || childType === 'TriggerOnClick'
          ? true
          : `${name} can only have one child!`)
      );
    },
    defaultChildren: ['IconButton'],
    defaultValue,
    props: {
      color: {
        label: 'Color',
        type: 'ThemeBGColor',
        filterColor: (color: string) => {
          return !['ioGrey', 'paper', 'success', 'warning', 'info'].includes(
            color,
          );
        },
      },
      variant: {
        label: 'Variant',
        type: 'select',
        options: badgeVariant,
      },
      badgeValueExpression: {
        label: 'Badge Value Expression',
        type: 'expressionOnly',
        returnType: () => 'number',
      },
      ...toolTipProps,
    },
    canHaveChildren: () => true,
    category: 'Toolbar',
    getDisplayText: (item) => item.props.badgeValueExpression,
    icon: { iconName: 'badge' },
    description: `**Overview**<br/>
The Badge component is a small visual indicator used to highlight numeric values, statuses, or alerts associated with another UI element. It typically appears as a small dot or pill superimposed on the corner of its child component (like an icon or button).
Use this component to display "notification counts" (e.g., unread emails), status flags, or to draw attention to specific items in the interface.

**1. Structure & Wrapper Logic**<br/>
The Badge functions as a wrapper component. You place the element you want to "decorate" inside the Badge component.
* **Single Child Policy:** The badge allows only one visual child (plus optional triggers). Common children include IconButton (e.g., a Bell icon), Button, Icon, Link, Box or Card.

**2. Configuration**<br/>
* **Badge Value:** An Expression that determines the number displayed inside the badge (e.g., $.session.unreadCount). If the value is 0, the badge is typically hidden.
* **Color:** Sets the background color of the badge (e.g., primary, secondary, error). Note that some standard colors like paper or grey might be filtered out to ensure the badge remains distinct and visible.
* **Variant:** Determines the visual style, such as standard (circle with number) or dot (small colored circle without text).

**3. Best Use Cases**<br/>
* **Notifications:** Placing a red "5" badge on a Bell icon in the header.
* **Shopping Carts:** Showing the number of items in a cart icon.
* **Status Indicators:** Adding a small "online" dot to a user avatar.
* **Inbox Counts:** Displaying unread message counts on a sidebar navigation link.`,
  };
};

export default BadgeManager;
